import classNames from 'classnames';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import throttle from 'lodash/throttle';
import { array, arrayOf, bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';

import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  currentUserHomeLocation,
  homeLocationToBounds,
  homeLocationToOrigin,
} from '../../util/homeLocation';
import {
  AREA_ERROR_TEXT,
  areaToOriginParam,
  coarseArea,
  knownBuyerArea,
  markNearMePromptDone,
  nearMePromptDone,
  parseOriginParam,
  parseRadiusKm,
  resolveBuyerArea,
} from '../../util/buyerArea';
import { radiusLabel } from './distanceFilter';
import NearMePrompt from './NearMePrompt/NearMePrompt';
import TopbarSearchForm from '../TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import {
  getQueryParamNames,
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../util/search';
import { propTypes } from '../../util/types';
import { parse } from '../../util/urlHelpers';
import { tireSizeLabel, rimSizeLabel } from '../../util/dimensionRoutes';

import { AvatarMedium, Breadcrumbs, H3, H5, NamedLink, Page, Button } from '../../components';
import { ensureUser } from '../../util/data';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PopularModels from './PopularModels/PopularModels';
import { findCategoryBySlug } from '../../util/categoryLinks';
import { profilePageLinkProps } from '../../util/profileSeoUrl';

import {
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  groupListingFieldConfigs,
  initialValues,
  omitLimitedListingFieldParams,
  pickListingFieldFilters,
  restoreSortOnLeavingMap,
  searchParamsPicker,
  validFilterParams,
  validUrlQueryParamsFromProps,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchMap from './SearchMap/SearchMap';
import WheelplaceSearchCard from './WheelplaceSearchCard/WheelplaceSearchCard';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import ListingGridSkeleton from './SearchResultsPanel/ListingGridSkeleton';
/* P25009-402 BR-2 — complete sets that fit the car the buyer just looked up.
 * For a staggered car this is the only complete answer that exists: its axles
 * take different sizes, so no single annons can cover both. */
import ReadyPackagesSection from './ReadyPackagesSection';
import ListingNumberMatch from './ListingNumberMatch/ListingNumberMatch';
import ViewToggle from './ViewToggle/ViewToggle';
import DimensionQuickSearch from './DimensionQuickSearch/DimensionQuickSearch';

/* The car bar — the one place the page names the buyer's car, with its sizes
 * and the "Byt bil" panel (P25009-491). */
import CarBar from './CarBar/CarBar';
import RememberedCarSearch from './CarBar/RememberedCarSearch';
import {
  CLOSE_TIRE_DIAMETER_TOLERANCE,
  CLOSE_TIRE_WIDTH_MM,
  FIT_LEVEL,
  plateInfoCar,
  plateInfoCarName,
  plateInfoModelName,
} from '../../util/wheelFitment';
import { carLabel as carLabelOf, isCarPayload, isCarsOwnSearch } from '../../util/savedCar';
import SortBy from './SortBy/SortBy';
import SaveSearchButton from './SaveSearchButton/SaveSearchButton';

import { CarInfoCard } from '../WP/CarInfoCard';
import { RecommendedDimensionsCard } from '../WP/RecommendedDimensionsCard';
import { SearchSection } from '../WP/LandingPage/SearchSection';
import { getDynamicSelectedPills } from './getDynamicSelectedPills';
import { rankListingsForCar } from '../../util/carSearchParams';
import { boltCircleSpellings } from '../../util/boltCircleCore';
import { buildIntentHeadline, buildIntentSubline, buildIntentCrumbs } from './searchIntent';
import { clientSortListings, fitBandOf, rankFitFirst } from './resultCardData';
import { fitSearchCar } from './carFitSearch';
import css from './SearchPage.module.css';
// import ShowOwnListings from './ShowOwnListings/ShowOwnListings';
// import ConnectedUsers from './ConnectedUsers';
import UserSelector from './UserSelector';
import OwnStockToggle from './OwnStockToggle/OwnStockToggle';
import { loadData, updateUserSavedQuery } from './SearchPage.duck';
const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.

const { LatLng, LatLngBounds } = sdkTypes;

/* Fallback bounds when there's nothing else to center the map on:
 * roughly the whole of Sweden. */
const SWEDEN_BOUNDS = new LatLngBounds(new LatLng(69.06, 24.17), new LatLng(55.0, 10.57));

/* Fit-all bounds from the geolocations of the current result page.
 * Pads single-listing results a little so the map doesn't open at
 * street-level zoom. */
const boundsFromListings = listings => {
  const points = (listings || [])
    .map(l => l?.attributes?.geolocation)
    .filter(g => g?.lat != null && g?.lng != null);
  if (points.length === 0) {
    return null;
  }
  const lats = points.map(p => p.lat);
  const lngs = points.map(p => p.lng);
  const pad = 0.05;
  return new LatLngBounds(
    new LatLng(Math.max(...lats) + pad, Math.max(...lngs) + pad),
    new LatLng(Math.min(...lats) - pad, Math.min(...lngs) - pad)
  );
};

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

/* Whether this page shows only what fits the buyer's car — the same rule the
 * search used (carFitSearch.fitSearchCar), read from the page's own props.
 * `isFitSearch`: the URL names the car — its plate, or for a car chosen by
 * model its carId (P25009-491). */
const pageFitState = ({ licensePlateInfo, licensePlateReg, location }) => {
  const params = new URLSearchParams(location?.search || '');
  const regNumber = params.get('regNumber') || '';
  const carId = params.get('carId') || '';
  const passarParam = params.get('passar');
  const fitCar = isCarPayload(licensePlateInfo) ? licensePlateInfo : null;
  const isFitSearch =
    !!fitCar && isCarsOwnSearch(params, { data: fitCar, reg: licensePlateReg });
  const onlyFits = !!fitSearchCar({
    plateInfo: licensePlateInfo,
    plateReg: licensePlateReg,
    regNumber,
    carId,
    passar: passarParam,
  });
  return { fitCar, isFitSearch, onlyFits, passarParam };
};

/**
 * The query without one result pill's condition — what its ✕ does, and, one
 * pill after another, what "Rensa" does. Plain object in, plain object out.
 */
export const paramsWithoutPill = (currentParams, pill) => {
  const params = { ...currentParams };

  // CATEGORY (level1 + level2)
  if (pill.key === 'category') {
    delete params.pub_categoryLevel1;
    delete params.pub_categoryLevel2;
  }

  /* "Inom 50 km" (P25009-425). The buyer's area (`origin`) stays: the cards
   * keep measuring from it, and a distance sort keeps ranking by it. */
  else if (pill.key === 'radius') {
    delete params.radius;
  }

  /* "235/60R18" — one chip for the three size params (P25009-486 BR-2). */
  else if (pill.key === 'tireSize') {
    delete params.pub_tireWidthFront;
    delete params.pub_profileFront;
    delete params.pub_diameter;
  }

  /* RANGE / OPTION-LIST / SET — one chip, one param. Splitting a hub-bore
   * list or an ET range value by value would leave a half-applied filter.
   * A bolt circle is one circle in several spellings ("139.7,139_7,1397",
   * P25009-488): the chip names the Console option, the others ride along. */
  else if (['etFront', 'etRear', 'hubHole', 'combination', 'boltCircle'].includes(pill.key)) {
    delete params[`pub_${pill.key}`];
    if (pill.key === 'combination') delete params.axle;
  }

  // MULTI-VALUE CSV PARAMS
  else {
    const paramKey = `pub_${pill.key}`;
    const rawValue = params[paramKey];
    if (!rawValue) return params;

    const values = String(rawValue).split(',');
    /* A chip can stand for several values ("Vinter" = both winter types). */
    const removing = String(pill.value).split(',');
    const updated = values.filter(v => !removing.includes(v));

    if (updated.length) {
      params[paramKey] = updated.join(',');
    } else {
      delete params[paramKey];
    }
  }
  return params;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    /* View mode — 'grid' (default), 'list' or 'map'. Read from localStorage
     * during constructor so the first render uses the stored value
     * instead of flashing through a default (same pattern as the
     * SearchField REG/Text toggle). */
    const initialViewMode = (() => {
      if (typeof window === 'undefined') return 'grid';
      try {
        const stored = window.localStorage.getItem('wheelplace.search.viewMode');
        return ['list', 'map'].includes(stored) ? stored : 'grid';
      } catch {
        return 'grid';
      }
    })();

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      value: 0,
      updateSuccessModalOpen: false,
      viewMode: initialViewMode,
      /* The sort param dropped when entering map view, so it can be
       * restored on the way back to grid/list. */
      removedSort: null,
      /* P25009-486 BR-2 — the desktop filter card folds as a whole. */
      filterPanelOpen: true,
      /* P25009-425 — the buyer's area lives partly in this browser's storage,
       * which the server render cannot read; the page uses it once mounted,
       * so the first client render matches the server's. */
      isMounted: false,
      /* "Närmast mig" is finding the buyer's position, and what to say when
       * it could not (inline — browsing never waits on it, BR-5). */
      locating: false,
      locationNotice: null,
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // View toggle
    this.handleViewModeChange = this.handleViewModeChange.bind(this);

    // Map view
    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
  }

  handleViewModeChange(mode) {
    const previousMode = this.state.viewMode;
    this.setState({ viewMode: mode });
    try {
      window.localStorage.setItem('wheelplace.search.viewMode', mode);
    } catch {
      /* storage quota / disabled — non-fatal, toggle still works for this session */
    }

    const { history, routeConfiguration, currentUser, location } = this.props;

    /* Entering map view with a saved ort and no location params in the
     * URL: push origin=ort so the Marketplace API returns the nearest
     * listings first — the pins on page 1 are then the ones closest to
     * the user's ort instead of an arbitrary page of results. */
    if (mode === 'map') {
      const { origin, bounds } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      /* The buyer's area — this browser's "Använd min plats" or the profile's
       * ort — rounded to about a kilometre (P25009-425 BR-6). */
      const originParam = areaToOriginParam(knownBuyerArea({ currentUser }));
      if (!origin && !bounds && originParam) {
        const urlQueryParams = validUrlQueryParamsFromProps(this.props);
        /* Remember the sort we drop here (distance ranking and an explicit
         * sort are mutually exclusive) so it can be restored when the user
         * switches back to grid/list. */
        this.setState({ removedSort: urlQueryParams.sort || null });
        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, {
            ...omit(urlQueryParams, 'sort'),
            origin: originParam,
          })
        );
      }
      return;
    }

    /* Leaving map view for grid/list: restore the sort map view dropped —
     * unless the user chose a different sort while in map view (then the
     * URL already carries their choice and restoreSortOnLeavingMap returns
     * null). */
    if (previousMode === 'map') {
      const urlQueryParams = validUrlQueryParamsFromProps(this.props);
      const restored = restoreSortOnLeavingMap(urlQueryParams, this.state.removedSort);
      if (restored) {
        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, restored)
        );
      }
      if (this.state.removedSort) {
        this.setState({ removedSort: null });
      }
    }
  }

  /* Same behaviour as SearchPageWithMap: once the user pans/zooms the
   * map, run a new search limited to the visible bounds. Debounced in
   * the constructor. */
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;

    const routes = this.props.routeConfiguration;
    const searchPagePath = pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    if (viewportBoundsChanged && isSearchPage && this.state.viewMode === 'map') {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
      };

      /* A distance chip gives way to the map: panning chooses the area now
       * (one area at a time, P25009-425). */
      // eslint-disable-next-line no-unused-vars
      const { address, bounds, mapSearch, radius, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
    }
  }

  componentDidMount() {
    this.setState({ isMounted: true });
  }

  componentWillUnmount() {
    this.props.onClearListingType();
  }

  /* The car's fit search (P25009-492) runs in loadData when the car is
   * already known. When it is not — the server render, or a car that reached
   * Redux after the search started (a new device, looked up from the
   * profile) — the page asks for it once per URL. */
  requestFitSearchIfMissing() {
    const {
      searchInProgress,
      searchLoadPending,
      fitResult,
      location,
      onRefreshSearch,
      config,
      params,
    } = this.props;
    if (searchInProgress || searchLoadPending || fitResult?.applied || !onRefreshSearch) return;
    if (!pageFitState(this.props).onlyFits) return;
    const search = location?.search || '';
    if (this.fitSearchRequestedFor === search) return;
    this.fitSearchRequestedFor = search;
    const { make, model, tireSize, rimSize } = this.props;
    onRefreshSearch(params || { make, model, tireSize, rimSize }, search, config);
  }

  /* Detect plate-driven 0-listing landings and mark them as
   * unmet demand. Fires once per (regNumber, search URL) tuple per
   * tab — the sessionStorage key prevents a re-render storm from
   * inflating the count. The actual aggregation lives in
   * /api/searchKeywords/markZeroResult. */
  componentDidUpdate(prevProps) {
    this.requestFitSearchIfMissing();
    const justFinished =
      prevProps.searchInProgress && !this.props.searchInProgress;
    if (!justFinished) return;

    const { pagination, location, licensePlateInfo, car } = this.props;
    const totalItems = pagination?.totalItems;
    if (totalItems !== 0) return;

    const params = parse(location?.search || '');
    const regNumber = params?.regNumber || '';

    /* The make this search is about. `carMake` is what the plate flows write
     * now — the make stopped being a filter (BR-2), so pub_categoryLevel1 is
     * only present when the buyer picked a brand by hand. Either one still
     * identifies the car; without both, the search is not about a car at all
     * (a keyword search, a browse) and must not be filed as unmet demand for
     * whatever car happens to be left in Redux. */
    const urlMake =
      (typeof params?.carMake === 'string' && params.carMake.trim()) ||
      (typeof params?.pub_categoryLevel1 === 'string' && params.pub_categoryLevel1.trim()) ||
      '';
    if (!urlMake) return;

    /* Three places the car identity might come from, in priority
     * order:
     *   1. state.car  — saved car for logged-in users
     *   2. licensePlateInfo — plate lookup made in this session
     *   3. pub_categoryLevel1 / pub_categoryLevel2 in the URL — fallback
     *      for paste-URL / reloaded-tab landings where neither Redux
     *      slice was hydrated. Same lowercased make/model the rest of
     *      the search system uses, so it lands in the same demand row
     *      a richer source would write to.
     * The tracker re-checks (1) and (2) against urlMake and drops them when
     * they disagree, so a stale saved car cannot claim someone else's miss. */
    const plateData = car?.car ? car : licensePlateInfo;
    const urlModel =
      (typeof params?.carModel === 'string' && params.carModel.trim()) ||
      (typeof params?.pub_categoryLevel2 === 'string' && params.pub_categoryLevel2.trim()) ||
      '';
    const titleCase = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
    const fallbackResolvedCar = (!plateData && urlMake)
      ? {
          make: urlMake.toLowerCase(),
          model: urlModel.toLowerCase(),
          variant: '',
          displayMake: titleCase(urlMake),
          displayModel: titleCase(urlModel),
          displayVariant: '',
          hp: null,
          year: null,
        }
      : null;
    if (!plateData && !fallbackResolvedCar) return;

    /* Capture which filter combo produced the empty result, so the dashboard
     * can distinguish "Volvo XC60: nothing at all" from "Volvo XC60 with
     * 225/45R17 wheels: 0 matches".
     *
     * What an entry here MEANS changed with this ticket. The search now
     * widens itself before giving up (SearchPage.duck), so a row only lands
     * here once even the widened query came back empty — a genuine supply
     * gap rather than a filter that was too strict. That is the distinction
     * the ticket asks the dashboard to make. */
    const adType = params?.pub_adType || '';
    const tireWidth = params?.pub_tireWidthFront || '';
    const profile = params?.pub_profileFront || '';
    /* A list ("18,19,20,21,22") is the car search's own diameters, not a
     * size anyone asked for (P25009-492) — filed like a search without one. */
    const diameter = String(params?.pub_diameter ?? '').includes(',')
      ? ''
      : params?.pub_diameter || '';
    const dimLabel = tireWidth && profile && diameter
      ? `${tireWidth}/${profile}R${diameter}`
      : (diameter ? `R${diameter}` : '');
    const label = [adType, dimLabel].filter(Boolean).join(' ').trim();
    const filter = (adType || tireWidth || profile || diameter)
      ? { adType, tireWidth, profile, diameter, label }
      : undefined;

    /* The tracker's per-tab 2-second window only collapses a re-render
     * storm. Counting each (session, car, filter) once is enforced on the
     * server, so it also holds for browsers still running a cached bundle.
     * The raw SearchEvent log keeps every repeat regardless, so the
     * "applied the filter again" signal is never lost — it just no longer
     * inflates the aggregate the dashboard reads. */
    import('../../util/searchKeywordTracker').then(m => {
      m.markZeroResult({
        data: plateData,
        resolvedCar: fallbackResolvedCar || undefined,
        rawInput: regNumber,
        filter,
        expectedMake: urlMake,
      });
    });
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    // const { listingFields: listingFieldsConfig } = config?.listing || {};
    // const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    // const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    // const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params
    // const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage',
      routeConfiguration,
      {},
      //  queryParams
      {}
    ));
  }

  handleSubmit(values, urlQueryParams) {
    const { currentSearchParams } = urlQueryParams;
    const { history, config, routeConfiguration } = this.props;

    const urlParams = new URLSearchParams(window.location.search);

    const persistentParams = {};
    ['authorId', 'showByUser', 'selectedUserId'].forEach(key => {
      if (urlParams.has(key)) {
        persistentParams[key] = urlParams.get(key);
      }
    });

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        // Rim bolt pattern suggestion
        if (values.numberOfBolts && values.boltCircle) {
          return {
            pub_numberOfBolts: values.numberOfBolts,
            // Every stored spelling of the circle (P25009-488).
            pub_boltCircle: boltCircleSpellings(values.boltCircle).join(',') || values.boltCircle,
            pub_adType: values.adType || 'rim',
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
          };
        }

        // Rim diameter suggestion
        if (values.diameter && values.adType === 'rim') {
          return {
            pub_diameter: values.diameter,
            pub_adType: values.adType,
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Rim brand suggestion
        if (values.categoryLevel1 && values.adType === 'rim') {
          return {
            pub_categoryLevel1: values.categoryLevel1,
            pub_categoryLevel2: values.categoryLevel2 || '',
            pub_adType: values.adType,
            keywords: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Tire dimension suggestion
        if (values.tireWidthFront && values.profileFront && values.diameter) {
          return {
            pub_tireWidthFront: values.tireWidthFront,
            pub_profileFront: values.profileFront,
            pub_diameter: values.diameter,
            keywords: undefined,
            pub_categoryLevel1: undefined,
            pub_categoryLevel2: undefined,
            pub_adType: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Category / subcategory suggestion
        if (values.categoryLevel1) {
          return {
            pub_categoryLevel1: values.categoryLevel1,
            pub_categoryLevel2: (values.categoryLevel2 || ''),
            keywords: undefined,
            pub_tireWidthFront: undefined,
            pub_profileFront: undefined,
            pub_diameter: undefined,
            pub_adType: undefined,
            pub_numberOfBolts: undefined,
            pub_boltCircle: undefined,
          };
        }

        // Plain keyword search
        return {
          keywords: values?.keywords,
          pub_categoryLevel1: undefined,
          pub_categoryLevel2: undefined,
          pub_tireWidthFront: undefined,
          pub_profileFront: undefined,
          pub_diameter: undefined,
          pub_adType: undefined,
          pub_numberOfBolts: undefined,
          pub_boltCircle: undefined,
        };
      }

      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...persistentParams,
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  }

  /* A result pill's ✕ (one pill), or "Rensa" (every pill on the page — the
   * buyer's filters, never the car or the product, BR-5). */
  handleRemovePill = pill => this.removePills([pill]);

  removePills = pills => {
    const { listingFields: listingFieldsConfig } = this.props.config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } =
      this.props.config?.search || {};
    const listingCategories = this.props.config.categoryConfiguration.categories;

    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const currentParams = pills.reduce(
      paramsWithoutPill,
      Object.fromEntries(new URLSearchParams(this.props.location.search).entries())
    );
    delete currentParams.page;

    const cleanedParams = cleanSearchFromConflictingParams(
      currentParams,
      filterConfigs,
      sortConfig
    );

    this.setState({ currentQueryParams: cleanedParams }, () => {
      this.props.history.push(
        createResourceLocatorString(
          'SearchPage',
          this.props.routeConfiguration,
          {},
          cleanedParams
        )
      );
    });
  };



  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config, make, model } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const categoryParams = {};
        if (make) categoryParams.pub_categoryLevel1 = make;
        if (model) categoryParams.pub_categoryLevel2 = model;
        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...categoryParams,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          // If we have categories in search, we should use the SearchPageMake or SearchPageMakeModel route
          // if we want to preserve the semantic URL path.
          const makeFromParams = search.pub_categoryLevel1;
          const modelFromParams = search.pub_categoryLevel2;
          const queryParams = omit(search, ['pub_categoryLevel1', 'pub_categoryLevel2']);

          const routeName = modelFromParams
            ? 'SearchPageMakeModel'
            : makeFromParams
              ? 'SearchPageMake'
              : 'SearchPage';

          const pathParams = {
            ...(makeFromParams && { make: makeFromParams }),
            ...(modelFromParams && { model: modelFromParams }),
          };

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
          );
        }
      };


      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    /* A distance chip needs its centre, so while one is on the buyer's area
     * (`origin`) stays in the URL whatever the sort. The search sends it to
     * the API only for the distance sort (P25009-425, SearchPage.duck). */
    const hasRadius = !!parseRadiusKm(new URLSearchParams(location?.search || '').get('radius'));
    this.setState({ locationNotice: null });

    /* "Bäst matchning" IS the default ranking — no sort param, no origin.
     * Clearing both is what makes it a real choice next to the other chips
     * instead of an unreachable state (P25009-445 BR-4). With a distance chip
     * on, the area stays and the sort says bestMatch out loud, since "an
     * origin and no sort" means "Närmast mig". */
    if (values === 'bestMatch') {
      const nextParams = hasRadius
        ? { ...urlQueryParams, [urlParam]: 'bestMatch' }
        : omit(urlQueryParams, [urlParam, 'origin']);
      history.push(
        createResourceLocatorString('SearchPage', routeConfiguration, {}, nextParams)
      );
      return;
    }

    /* "Närmast mig" is a synthetic sort. Instead of a Sharetribe sort key it
     * relies on the `origin=lat,lng` search param — the Marketplace API
     * returns results ordered by distance when origin is set (and no sort).
     * The area is the one the page already knows (the URL, this browser's
     * "Använd min plats", the profile's ort); only when there is none is the
     * browser asked. Never more precise than an area (BR-6), and a refusal
     * is said inline — browsing goes on without it (BR-5). */
    if (values === 'nearMe') {
      const urlOrigin = new URLSearchParams(location?.search || '').get('origin');
      this.setState({ locating: true });
      resolveBuyerArea({ urlOrigin, currentUser: this.props.currentUser })
        .then(area => {
          markNearMePromptDone('answered');
          this.setState({ locating: false });
          const nextParams = {
            ...omit(urlQueryParams, urlParam),
            origin: areaToOriginParam(area),
          };
          history.push(
            createResourceLocatorString('SearchPage', routeConfiguration, {}, nextParams)
          );
        })
        .catch(err => {
          this.setState({
            locating: false,
            locationNotice: AREA_ERROR_TEXT[err?.reason] || AREA_ERROR_TEXT.unavailable,
          });
        });
      return;
    }

    /* Switching away from "Närmast mig" → also drop the origin param so
     * subsequent sorts run globally, not distance-first — unless a distance
     * chip is drawn around it. */
    const baseParams = hasRadius ? urlQueryParams : omit(urlQueryParams, 'origin');
    const queryParams = values
      ? { ...baseParams, [urlParam]: values }
      : omit(baseParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings: loadedListings,
      relaxedFilters = [],
      fitResult,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams,
      routeConfiguration,
      config,
      listingType,
      currentUser,
      adverts = [],
      connectedUsers,
      getConnectedUsersInProgress,
      userListingCounts,
      fetchUserListingCountsInProgress,
      editingQueryId,
      OnUpdateSavedQuery,
      updateSavedQueryInProgress,
      make,
      model,
      listingNumberMatch,
    } = this.props;

    const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    /* Where the cards measure "ca 12 km" from (P25009-425 BR-3): the search's
     * own origin, else — once mounted, because the server cannot read this
     * browser's storage — the area the buyer gave before or the profile's
     * ort. Always an area, never a more precise point (BR-6). */
    const buyerArea = this.state.isMounted
      ? knownBuyerArea({ urlOrigin: origin, currentUser })
      : coarseArea(parseOriginParam(origin));

    /* The car this search is about. Written by every plate flow as plain
     * carMake/carModel params — never a filter, so a wheel that fits but was
     * tagged with another model still shows up, just further down (BR-2). */
    const carSearchContext = (() => {
      const params = new URLSearchParams(location.search || '');
      return {
        regNumber: params.get('regNumber') || '',
        make: params.get('carMake') || '',
        model: params.get('carModel') || '',
      };
    })();
    const rankedListings = rankListingsForCar(loadedListings, carSearchContext);

    /* Fit first (P25009-486 BR-1). On the car's own search (the URL names the
     * car's plate) the page shows ONLY what fits, by default; passar=0 ("Visa
     * även ej passande") shows the rest too, and passar=1 still forces
     * fit-only on any page with a car (P25009-482).
     *
     * The fit check runs in the search itself, over every result, before the
     * results are paged (carFitSearch.js, P25009-492) — `fitResult` says so.
     * Until that search has run (the car reached Redux after it), the loaded
     * page is filtered here as a stop-gap and componentDidUpdate asks for the
     * fit search.
     *
     * Listings untouched for 12 months are shown again: the client chose that
     * over hiding them (P25009-492, reversing P25009-486 BR-3). */
    const { fitCar, isFitSearch, onlyFits } = pageFitState(this.props);
    const fitApplied = onlyFits && !!fitResult?.applied;
    const filterPageHere = onlyFits && !fitApplied;
    const listings = filterPageHere
      ? rankedListings.filter(l => fitBandOf(l, fitCar) === 0)
      : rankedListings;
    const hiddenNotFitting = filterPageHere ? rankedListings.length - listings.length : 0;
    const closeSizes = fitApplied && fitResult.level === FIT_LEVEL.CLOSE;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const value = listingType.listingType ? listingType.listingType : this.state.value;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      make: this.props.make,
      model: this.props.model,
    });

    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };







    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
          : null,
      };
    };
    const initialSearchFormValues = topbarSearcInitialValues();

    const handleUpdateSavedQuery = async () => {
      const searchParams = new URLSearchParams(location.search);

      const excludeKeys = ["searchType", "page", "selectedUserId", "stock", "regNumber"];

      const filteredParams = {};

      for (let [key, value] of searchParams.entries()) {
        if (!excludeKeys.includes(key)) {
          filteredParams[key] = value;
        }
      }

      const payload = {
        id: editingQueryId,
        params: filteredParams,
      };

      await OnUpdateSavedQuery(payload);
      this.setState({ updateSuccessModalOpen: true });
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    /* Page-local re-rank for the sorts the Marketplace API cannot express
     * (P25009-445 BR-4, see resultCardData.js). Stable, so inside an equal
     * band the car-fit ranking above still decides the order. */
    /* No sort chosen = "Passar bäst" (fit, in stock, lowest total — BR-1).
     * Whatever the sort, "Passar inte" never ranks above a fitting card. */
    const clientSortKey = validQueryParams.sort || (origin ? null : 'bestMatch');
    const sortedListings = rankFitFirst(
      clientSortListings(listings, clientSortKey, { car: fitCar }),
      fitCar
    );

    /* Chips for the conditions the config-driven pill builder cannot see.
     *
     * `urlQueryParams` is the validated set, so anything that is not a
     * declared listing field — the car itself, a complete-set query — used to
     * be applied without ever being shown. An empty result then had no
     * explanation on screen. Every one of them is a chip now, and every chip
     * removes the condition it names (BR-5, AC-5).
     *
     * A range or a long option list becomes ONE chip, not one per value: the
     * hub-bore filter alone would otherwise put 20-odd chips on the page. */
    const rawSearchParams = new URLSearchParams(location.search || '');
    /* URL params carry lowercased slugs ("volvo", "xc60"). Model codes with
     * a digit are written in caps (XC60, V60, CX-5), plain words get a
     * capital ("golf" → Golf, "3-series" → 3-Series). */
    const prettyCarPart = part =>
      String(part)
        .split(/([\s-]+)/)
        .map(token =>
          /\d/.test(token) && /[a-zåäö]/i.test(token)
            ? token.toUpperCase()
            : token.charAt(0).toUpperCase() + token.slice(1)
        )
        .join('');
    /* On the car's own search the car itself names it ("Volkswagen ID.4" —
     * the URL's slug lost the dot, "id4"); otherwise the slugs, prettified. */
    const ownCar = isFitSearch && fitCar ? plateInfoCar(fitCar) : null;
    const ownCarLabel = ownCar
      ? [ownCar.brand || ownCar.make?.name, plateInfoModelName(fitCar)].filter(Boolean).join(' ')
      : '';
    const carLabel =
      ownCarLabel ||
      [carSearchContext.make, carSearchContext.model]
        .filter(Boolean)
        .map(prettyCarPart)
        .join(' ');
    const etValue = rawSearchParams.get('pub_etFront') || '';
    const hubValue = rawSearchParams.get('pub_hubHole') || '';
    const combinationValue = rawSearchParams.get('pub_combination') || '';
    const hubLabelFor = value => {
      const first = String(value).split(',')[0];
      const asNumber = first ? first.replace('_', ',') : '';
      return asNumber ? `Navhål från ${asNumber} mm` : 'Navhål';
    };

    /* The car itself is never a pill: it is in the car bar (P25009-491 BR-5). */
    const carPills = [
      etValue
        ? { key: 'etFront', value: etValue, label: `ET ${etValue.replace(',', ' till ')}` }
        : null,
      hubValue ? { key: 'hubHole', value: hubValue, label: hubLabelFor(hubValue) } : null,
      combinationValue
        ? {
            key: 'combination',
            value: combinationValue,
            label: `Komplett sats: ${combinationValue.replace(/^has_(all|any):/, '').replace(/,/g, ' + ')}`,
          }
        : null,
    ].filter(Boolean);

    /* One chip per thing a buyer chose (P25009-486 BR-2): the tyre size as
     * "235/60R18" rather than three "Däckbredd / Profil / Diameter" chips,
     * and "Vinter" rather than its two tyre types. */
    const dynamicPills = getDynamicSelectedPills(
      listingFieldsConfig,
      urlQueryParams,
      listingCategories
    ).filter(
      /* Built above as one chip each, with a readable label. */
      pill => !['etFront', 'hubHole'].includes(pill.key)
    );
    const tireWidthParam = rawSearchParams.get('pub_tireWidthFront');
    const profileParam = rawSearchParams.get('pub_profileFront');
    const diameterParam = rawSearchParams.get('pub_diameter');
    const sizePill =
      tireWidthParam && profileParam && diameterParam
        ? {
            key: 'tireSize',
            value: 'tireSize',
            label: `${tireWidthParam}/${profileParam}R${diameterParam}`,
          }
        : null;
    const SIZE_KEYS = ['tireWidthFront', 'profileFront', 'diameter'];
    /* The car's own diameters on a Hjul / Däck / Fälg search (P25009-492):
     * one fit chip, "Fälgdiameter 18–22\"", not five "Diameter: 18" chips. */
    const diameterValues = (diameterParam || '').split(',').filter(Boolean);
    const diametersInOrder = diameterValues.map(Number).sort((a, b) => a - b);
    const diametersContiguous = diametersInOrder.every(
      (d, i) => i === 0 || d === diametersInOrder[i - 1] + 1
    );
    const carDiametersPill =
      !sizePill && diameterValues.length > 1
        ? {
            key: 'diameter',
            value: diameterValues.join(','),
            label: `Fälgdiameter ${
              diametersContiguous
                ? `${diametersInOrder[0]}–${diametersInOrder[diametersInOrder.length - 1]}`
                : diametersInOrder.join(', ')
            }"`,
            group: 'fit',
          }
        : null;
    const tiretypeValues = (rawSearchParams.get('pub_tiretype') || '').split(',');
    const seasonPill =
      tiretypeValues.includes('winterFriction') && tiretypeValues.includes('winterDubbed')
        ? { key: 'tiretype', value: 'winterFriction,winterDubbed', label: 'Vinter' }
        : null;
    /* "Inom 50 km" (P25009-425 BR-2): a filter the buyer chose, so a pill. */
    const radiusKm = parseRadiusKm(rawSearchParams.get('radius'));
    const radiusPill = radiusKm
      ? { key: 'radius', value: String(radiusKm), label: radiusLabel(radiusKm) }
      : null;
    /* The car's technical conditions fold into one "Passformsfilter (n)" chip
     * that opens to show them — they are the car's, not the buyer's picks. */
    /* A chosen set ("Komplett sats: …") is the buyer's pick from the car
     * bar's size chips, so it is a plain pill, not one of these. */
    const FIT_GROUP_KEYS = [
      'etFront',
      'etRear',
      'hubHole',
      'numberOfBolts',
      'boltCircle',
      'rimWidthFront',
      'rimWidthRear',
    ];
    const allPills = [
      ...carPills,
      ...(radiusPill ? [radiusPill] : []),
      ...(sizePill ? [sizePill] : []),
      ...(seasonPill ? [seasonPill] : []),
      ...(carDiametersPill ? [carDiametersPill] : []),
      ...dynamicPills.filter(
        pill =>
          !(sizePill && SIZE_KEYS.includes(pill.key)) &&
          !(carDiametersPill && pill.key === 'diameter') &&
          !(
            seasonPill &&
            pill.key === 'tiretype' &&
            ['winterFriction', 'winterDubbed'].includes(String(pill.value))
          )
      ),
    ].map(pill => (FIT_GROUP_KEYS.includes(pill.key) ? { ...pill, group: 'fit' } : pill));
    /* Only the real filter pills (P25009-491 BR-5): never the product (the car
     * bar's Hjul / Däck / Fälg), and on the car's own search never the car's
     * own measurements — the bolt pattern, offset window, hub bore and wheel
     * diameters its search applies. Those are the car, shown in the bar. */
    const pills = allPills.filter(
      pill => pill.key !== 'adType' && !(isFitSearch && pill.group === 'fit')
    );
    /* "Filter · n aktiva" / "Filter (n)": what the buyer narrowed by — not the
     * car itself, not the sort or the map. */
    const activeFilterCount = pills.length;

    /* Which of the car's own conditions had to be given up for this result to
     * be non-empty. Named in Swedish, in the order they were dropped. */
    /* Width and profile are relaxed together and name one thing to a
     * shopper, so they share a label and are deduped below. */
    const RELAXED_LABELS = {
      pub_rimWidthFront: 'fälgbredd',
      pub_hubHole: 'navhål',
      pub_etFront: 'ET',
      pub_tireWidthFront: 'däckdimension',
      pub_profileFront: 'däckdimension',
    };
    /* "fälgbredd, navhål och ET" — a plain Swedish list, not "a och b och c". */
    const swedishList = items =>
      items.length <= 1
        ? items.join('')
        : `${items.slice(0, -1).join(', ')} och ${items[items.length - 1]}`;
    const relaxedLabels = [
      ...new Set(relaxedFilters.map(key => RELAXED_LABELS[key] || key)),
    ];
    /* Say only what the widened search still holds to. The bolt pattern always
     * stays; the diameter when the search had one (since P25009-492 every car
     * search does: the clicked size, or the car's own diameters). "Som
     * passar" over cards that read "Passar inte" contradicted itself. */
    const keptFacts = urlQueryParams?.pub_diameter != null ? 'diameter och bultmönster' : 'bultmönster';
    const relaxedNotice =
      relaxedLabels.length > 0 ? (
        <p className={css.relaxedNotice} role="status">
          Ingen annons matchade <strong>{swedishList(relaxedLabels)}</strong> för din bil. Vi visar
          i stället annonser med samma {keptFacts} — de passar inte säkert, så kontrollera måtten i
          annonsen innan du köper.
        </p>
      ) : null;

    /* The green fit bar (P25009-486 BR-1): what the list is, what was
     * checked, and the way to the rest. The Din bil box sits in the filter
     * sheet on a phone, so the toggle must also live here. */
    const setPassar = value => {
      const next = new URLSearchParams(location.search || '');
      if (value) next.set('passar', value);
      else next.delete('passar');
      const search = next.toString();
      this.props.history.push({ search: search ? `?${search}` : '' });
    };
    /* Every result was checked when the search did the fit check
     * (fitApplied); only the stop-gap filter above speaks of "this page". */
    const morePages = (pagination?.totalPages || 1) > 1;
    const onThisPage = filterPageHere && morePages ? ' på den här sidan' : '';
    const productNoun =
      { wheel: 'hjul', tire: 'däck', rim: 'fälgar' }[rawSearchParams.get('pub_adType')] || 'delar';
    /* A car chosen by model is named by its version (P25009-491). */
    const fitCarName =
      (fitCar && (carLabelOf(fitCar, this.props.licensePlateReg) || plateInfoCarName(fitCar))) ||
      'bil';
    /* Only what this search really held the listings to: the bolt pattern and
     * the size always; the hub bore and ET while the search still filters on
     * them (a widened search may have dropped them — relaxedFilters). */
    const checkedFacts = [
      'Bultmönster',
      rawSearchParams.get('pub_hubHole') && !relaxedFilters.includes('pub_hubHole')
        ? 'navhål'
        : null,
      rawSearchParams.get('pub_etFront') && !relaxedFilters.includes('pub_etFront') ? 'ET' : null,
      productNoun === 'fälgar' ? 'fälgdiameter' : 'däckdimension',
    ].filter(Boolean);
    const checkedLine = `${checkedFacts.slice(0, -1).join(', ')} och ${
      checkedFacts[checkedFacts.length - 1]
    } kontrollerade per annons.`;
    const hiddenLine =
      hiddenNotFitting > 0
        ? ` ${hiddenNotFitting} som inte passar är ${
            hiddenNotFitting === 1 ? 'dold' : 'dolda'
          }${onThisPage}.`
        : '';
    /* No listing in one of the car's own sizes, so the search fell back to
     * close ones (P25009-492) — said plainly, with what "close" means. */
    const closeLine = `Ingen annons har någon av bilens originalstorlekar. De här har samma fälgdiameter, en däckbredd högst ${CLOSE_TIRE_WIDTH_MM} mm ifrån och en rullomkrets inom ${Math.round(
      CLOSE_TIRE_DIAMETER_TOLERANCE * 100
    )} % av en originalstorlek. Kontrollera storleken innan du köper.`;
    const noneFit = onlyFits && listings.length === 0 && (fitApplied || hiddenNotFitting > 0);
    /* The car bar's "Bara det som passar" switch says whether only fitting
     * cards show; the green "Visar bara … som passar" bar that repeated it is
     * gone (P25009-491 BR-2). What stays is what the switch cannot say: that
     * nothing passed the check, or that the list is close sizes only
     * (P25009-492). */
    const fitBar =
      fitCar && onlyFits && (noneFit || closeSizes) ? (
        <div
          className={classNames(css.fitBar, closeSizes ? css.fitBarClose : css.fitBarOff)}
          role="status"
        >
          <span className={css.fitBarIcon} aria-hidden="true">
            {closeSizes ? '≈' : '!'}
          </span>
          <div className={css.fitBarText}>
            {noneFit ? (
              <strong>
                Inga {productNoun} för din {fitCarName} klarar passformskontrollen{onThisPage}
              </strong>
            ) : (
              <strong>
                Inga {productNoun} i originalstorlek för din {fitCarName} – visar närliggande
                storlekar
              </strong>
            )}
            <span className={css.fitBarDetail}>
              {closeSizes ? closeLine : `${checkedLine}${hiddenLine}`}
            </span>
          </div>
          {noneFit ? (
            <button type="button" className={css.fitBarToggle} onClick={() => setPassar('0')}>
              Visa även ej passande
            </button>
          ) : null}
        </div>
      ) : null;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    /* The buyer's own filters, as chips — not sort, map bounds or the car. */
    const selectedFiltersCountForMobile = activeFilterCount;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems = filterPageHere
      ? listings.length
      : // searchParamsAreInSync &&
      hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
          ? listings.length
          : 0;
    const totalAdverts = adverts.length;
    const listingsAreLoaded =
      !searchInProgress
      // && searchParamsAreInSync
      &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    /* Reflect "Närmast dig" as the current selection when the URL has
     * an origin param — the sort key itself isn't stored (Sharetribe
     * sorts by origin natively) so we synthesise the key here. Read the
     * origin off the parsed URL, NOT validQueryParams: searchParamsPicker
     * strips origin behind the same isOriginInUse gate that pinned the
     * old nearMe sort dead (P25009-445). */
    const hasOrigin = !!origin;
    const resolvedSort = hasOrigin && !validQueryParams[sortConfig.queryParamName]
      ? 'nearMe'
      : validQueryParams[sortConfig.queryParamName];
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={resolvedSort}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
          /* At the right end of the results toolbar on a computer, and of
           * the phone's toolbar row: the menu opens inwards. */
          contentPosition="right"
        />
      ) : null;
    };
    /* All of the car's results hidden by the fit check is not "no results":
     * they are there and fail it (or are a year old). The fit bar says so,
     * with the way back; the generic empty state would offer to widen
     * filters instead. */
    const noResultsInfo =
      noneFit ? null : (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { title, description, schema, noindex } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      {
        make: this.props.make || searchParamsInURL?.pub_categoryLevel1,
        model: this.props.model || searchParamsInURL?.pub_categoryLevel2,
        tireSize: this.props.tireSize,
        rimSize: this.props.rimSize,
        totalItems,
      }
    );

    // The URL segments are lower-cased category ids, so showing them raw gave a
    // brand page the heading "volvo" and a model page "volvo xc60". Read the names
    // from the category configuration instead, and fall back to the segment when
    // the category is unknown — SearchPage answers 200 for those rather than 404.
    const makeCategory = findCategoryBySlug(config.categoryConfiguration?.categories, make);
    const modelCategory = findCategoryBySlug(makeCategory?.subcategories, model);
    const makeLabel = makeCategory?.name || make;
    const modelLabel = modelCategory?.name || model;

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>

    const handleChange = (event, newValue) => {
      this.setState({ value: newValue });
      this.props.onClearListingType();
    };

    /* Product-type toggle in the sidebar (DimensionQuickSearch). Maps
     * the adType to the same `value` the filter list keys off, so
     * picking Däck/Fälg/Hjul both narrows results (via pub_adType in
     * the URL) and swaps which filters render below. */
    const adTypeToValue = { wheel: 0, tire: 1, rim: 2 };
    const valueToAdType = { 0: 'wheel', 1: 'tire', 2: 'rim' };
    /* Prefer the URL's adType (deep-links, saved searches) so the toggle
     * reflects the actual result set; fall back to the local `value`. */
    const urlAdType = validQueryParams?.pub_adType;
    const activeAdType =
      urlAdType && adTypeToValue[urlAdType] !== undefined
        ? urlAdType
        : valueToAdType[Number(value)] || 'tire';
    const handleSelectAdType = type => {
      this.setState({ value: adTypeToValue[type] ?? 0 });
      this.props.onClearListingType();
    };

    /* Navhål filter renders as a discrete slider over the field's real
     * enum values — hand its config (enumOptions/schemaType/searchMode)
     * to the quick-search card. */
    const hubHoleConfig = (listingFieldsConfig || []).find(c => c.key === 'hubHole') || null;

    /* Skick + Fälgtyp render as toggles in the quick-search card, driven
     * by the hosted field configs (same pattern as navhål). Pris gets the
     * default price filter's bounds for its range slider. */
    const conditionConfig = (listingFieldsConfig || []).find(c => c.key === 'condition') || null;
    const rimTypeConfig = (listingFieldsConfig || []).find(c => c.key === 'rimType') || null;
    const priceFilterConfig = (defaultFiltersConfig || []).find(f => f.key === 'price') || null;

    const handleClearUser = () => {
      this.props.history.push(
        createResourceLocatorString('SearchPage', routeConfiguration, {}, {})
      );
    };

    const tireSizeText = this.props.tireSize ? tireSizeLabel(this.props.tireSize) : null;
    const rimSizeText = this.props.rimSize ? rimSizeLabel(this.props.rimSize) : null;
    const dimensionHeading = tireSizeText
      ? {
          h1: `Däck ${tireSizeText}`,
          intro: `Bläddra bland nya och begagnade däck i dimension ${tireSizeText}. Jämför pris och skick och hitta rätt däck för din bil.`,
        }
      : rimSizeText
        ? {
            h1: `Fälgar ${rimSizeText}`,
            intro: `Bläddra bland nya och begagnade fälgar ${rimSizeText}. Jämför pris och skick och hitta rätt fälgar för din bil.`,
          }
        : null;

    /* Intent headline (P25009-445 BR-1): the H1 states what the list IS —
     * "Vinterdäck 235/55R19 till din Volvo XC60" — composed from the active
     * filters, with the old make/model heading as the fallback. The subline
     * only claims what the loaded data proves (see searchIntent.js). */
    const intentHeadline = buildIntentHeadline({
      queryParams: validQueryParams,
      carLabel,
    });
    const intentSubline = listingsAreLoaded
      ? buildIntentSubline({ totalItems, listings, pagination })
      : null;
    const intentCrumbs = make ? [] : buildIntentCrumbs(validQueryParams);

    /* Staggered set search (P25009-364). `axle` is written by the car panel
     * when the buyer deliberately narrows a complete set to one axle. It never
     * reaches the API — it exists so these results can say plainly that they
     * only cover half the car (AC-2). */
    const axleParam = new URLSearchParams(location.search || '').get('axle');
    const singleAxleNotice =
      axleParam === 'front' || axleParam === 'rear' ? (
        <p className={css.singleAxleNotice} role="status">
          Visar endast <strong>{axleParam === 'front' ? 'framdäck' : 'bakdäck'}</strong> — dessa
          annonser täcker bara en axel och räcker inte till en komplett sats för din bil.
        </p>
      ) : null;

    /* "Närmast mig" looking for, or not finding, the buyer's position — said
     * here instead of in a browser alert, and the list stays as it was
     * (P25009-425 BR-5). */
    const locationNoticeLine =
      this.state.locating || this.state.locationNotice ? (
        <p className={css.singleAxleNotice} role="status">
          {this.state.locating ? 'Hämtar din plats …' : this.state.locationNotice}
        </p>
      ) : null;

    /* BR-5 / AC-3 — asked once, only to a buyer whose area the page does not
     * know, only over a list with something in it, and never in the seller's
     * own-stock mode or on the map (which has its own way to pick an area). */
    const isOwnStockMode = rawSearchParams.get('searchType') === 'ownListing';
    const showNearMePrompt =
      this.state.isMounted &&
      !buyerArea &&
      !this.state.locating &&
      !this.state.locationNotice &&
      !nearMePromptDone() &&
      listingsAreLoaded &&
      totalItems > 0 &&
      !isOwnStockMode &&
      this.state.viewMode !== 'map';
    const nearMePrompt = showNearMePrompt ? (
      <NearMePrompt
        busy={this.state.locating}
        onAccept={() => {
          /* A yes is the answer even when no position comes of it. */
          markNearMePromptDone('answered');
          this.handleSortBy(sortConfig.queryParamName, 'nearMe');
        }}
        onDismiss={() => {
          markNearMePromptDone('dismissed');
          this.forceUpdate();
        }}
      />
    ) : null;

    const ListingsPanel = props => {
      // const selectedListings = listings.filter(listing => {
      //   return listing.attributes.publicData.listingType === props.listingType;
      // });

      return (
        <>
          {dimensionHeading ? (
            <header className={css.dimensionHeader}>
              <h1 className={css.dimensionH1}>{dimensionHeading.h1}</h1>
              {listingsAreLoaded ? (
                <p className={css.dimensionCount}>{totalItems} annonser</p>
              ) : null}
              <p className={css.dimensionIntro}>{dimensionHeading.intro}</p>
            </header>
          ) : null}
          <MainPanelHeader
            className={css.mainPanel}
            /* One line (P25009-491 BR-5): "N resultat", the filter pills,
             * Rensa, the sort as a dropdown ("Passar bäst ▾") and Bevaka. */
            sortByComponent={sortBy('desktop')}
            saveSearchComponent={<SaveSearchButton label="Bevaka" withIcon />}
            onClearPills={pills.length > 0 ? () => this.removePills(pills) : null}
            viewToggleComponent={
              <ViewToggle
                mode={this.state.viewMode}
                onChange={this.handleViewModeChange}
              />
            }
            isSortByActive={sortConfig.active}
            listingsAreLoaded={listingsAreLoaded}
            resultsCount={totalItems}
            totalAdverts={totalAdverts}
            searchInProgress={searchInProgress}
            searchListingsError={searchListingsError}
            noResultsInfo={noResultsInfo}
            pills={pills}
            onRemovePill={this.handleRemovePill}
          />
          {fitBar}
          {singleAxleNotice}
          {relaxedNotice}
          {locationNoticeLine}
          {nearMePrompt}
          <div
            id="searchResultsAnchor"
            className={classNames(css.listingsForGridVariant, {
              [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
            })}
          >
            {searchListingsError ? (
              <div className={css.searchErrorBox}>
                <H3 className={css.error}>
                  <FormattedMessage id="SearchPage.searchError" />
                </H3>
                {/* A failed search is almost always a dropped connection
                  * ("Network Error"), and the message used to say "try again"
                  * with no way to do it but leaving the page. Reloading runs
                  * the same URL — same filters, same car — once more. */}
                <button
                  type="button"
                  className={css.searchRetryButton}
                  onClick={() => window.location.reload()}
                >
                  Försök igen
                </button>
              </div>
            ) : null}
            {!isValidDatesFilter ? (
              <H5>
                <FormattedMessage id="SearchPage.invalidDatesFilter" />
              </H5>
            ) : null}
            {/* <div className={css.flexContent}>
              <ShowOwnListings intl={intl} currentUser={currentUser} />
              <ConnectedUsers
                connectedUsers={connectedUsers}
                getConnectedUsersInProgress={getConnectedUsersInProgress}
                userListingCounts={userListingCounts}
                fetchUserListingCountsInProgress={fetchUserListingCountsInProgress}
                history={this.props.history}
              >

              </ConnectedUsers>
            </div> */}

            {/* P25009-485 BR-4 — business sellers only; renders nothing otherwise. */}
            <OwnStockToggle currentUser={currentUser} history={this.props.history} />
            <div className={css.flexContent}>
              <UserSelector
                intl={intl}
                currentUser={currentUser}
                connectedUsers={connectedUsers}
                getConnectedUsersInProgress={getConnectedUsersInProgress}
                userListingCounts={userListingCounts}
                fetchUserListingCountsInProgress={fetchUserListingCountsInProgress}
                history={this.props.history}
              />
            </div>
            {/* The "Dina bilar" chip row that sat here is gone: saved cars are
              * picked in the car bar's "Byt bil" panel (P25009-491 BR-1). The
              * update button belongs to editing a saved search from Dina
              * bilar, so it stays. */}
            <div className={css.savedQueriesWrapperWithButton}>
              {editingQueryId ? <div className={css.updateQueryButtonWrapper}>
                <Button
                  onClick={handleUpdateSavedQuery}
                  className={css.updateQueryButton}
                  inProgress={updateSavedQueryInProgress}
                  disabled={updateSavedQueryInProgress}
                >Uppdatera fråga</Button>
              </div> : null}
              {this.state.updateSuccessModalOpen ? (
                <div className={css.toastSuccess} role="status">
                  Frågan har uppdaterats
                </div>
              ) : null}

            </div>

            {/* P25009-512 BR-7 — a listing number: open its listing, or name
              * the sellers when several share it. Outside the view switch, so
              * it works in the map view and in the server render too. */}
            <ListingNumberMatch match={listingNumberMatch} listings={loadedListings} />

            {this.state.viewMode === 'map' ? (
              (() => {
                /* Map centering priority:
                 * 1. bounds/origin from the URL (user searched a place or
                 *    panned the map — mapSearch keeps this up to date),
                 * 2. the ort saved at signup / profile settings,
                 * 3. fit-all bounds of the current result page,
                 * 4. the whole of Sweden. */
                const homeLocation = currentUserHomeLocation(currentUser);
                const homeBounds = homeLocationToBounds(homeLocation);
                const homeOrigin = homeLocationToOrigin(homeLocation);
                let mapBounds = null;
                let mapCenter = null;
                if (bounds) {
                  mapBounds = bounds;
                } else if (origin) {
                  mapCenter = origin;
                } else if (homeBounds) {
                  mapBounds = homeBounds;
                } else if (homeOrigin) {
                  mapCenter = homeOrigin;
                } else {
                  mapBounds = boundsFromListings(listings) || SWEDEN_BOUNDS;
                }
                return (
                  <div className={css.gridMapWrapper} data-testid="searchMapContainer">
                    <SearchMap
                      reusableContainerClassName={css.gridMap}
                      bounds={mapBounds}
                      center={mapCenter}
                      location={location}
                      listings={sortedListings || []}
                      onMapMoveEnd={this.onMapMoveEnd}
                      onCloseAsModal={() => {
                        onManageDisableScrolling('SearchPage.map', false);
                      }}
                      messages={intl.messages}
                    />
                  </div>
                );
              })()
            ) : searchInProgress && (!listings || listings.length === 0) ? (
              <ListingGridSkeleton count={8} />
            ) : (
              <>
                {/* BR-2 — above the loose rims and tyres, because a complete
                  * set that already fits is the better answer to "what goes on
                  * my car". Renders nothing when no package fits. */}
                <ReadyPackagesSection
                  car={{
                    ...carSearchContext,
                    /* P25009-489 — the car's own minimum wheel size, so a
                     * package built for an older generation of the model
                     * (an XC40 at 17") is not offered to an EX40 (19"). */
                    minDiameter: isFitSearch ? plateInfoCar(fitCar)?.min_wheel_size || null : null,
                  }}
                />
                {/* No dealer ad blocks between the results (P25009-486 BR-1). */}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={sortedListings}
                  removeAds
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(location.search)}
                  handleClearUser={handleClearUser}
                  isMapVariant={false}
                  viewMode={this.state.viewMode}
                  searchOrigin={buyerArea}
                />
              </>
            )}
          </div>
        </>
      );
    };

    /* Skick and price belong to every category, so both lists carry
     * them (they are card-owned below, but the intent should read here
     * too). Note the key is `rimType` — a `rimTYpe` typo used to make
     * Fälgtyp vanish from the Fälg category. */
    const rimKeys = [
      'county',
      'condition',
      'diameter',
      'price',
      'rimType',
      'numberOfBolts',
      'boltCircle',
      'rimWidthFront',
      'rimWidthRear',
      'etFront',
      'etRear',
      'hubHole',
    ];
    const tireKeys = [
      'county',
      'condition',
      'diameter',
      'price',
      'tireWidthFront',
      'tireWidthRear',
      'profileFront',
      'profileRear',
      'season',
      'brand',
    ];

    /* Filters now owned by DimensionQuickSearch at the top of the list —
     * drop them from the dropdown list so we don't render the same
     * control twice. Product type → the 3-step toggle; season → the
     * season toggle; width/profile/diameter → the typed dimension rows;
     * skick → the Alla/Nya/Begagnade toggle; fälgtyp → a toggle;
     * price → a range slider (same query params, so the card's fields
     * fully replace the dropdowns). */
    const toggleOwnedKeys = [
      'adType',
      'rimType',
      'condition',
      'price',
      'tiretype',
      'season',
      'categoryLevel',
      'tireWidthFront',
      'tireWidthRear',
      'profileFront',
      'profileRear',
      'rimWidthFront',
      'rimWidthRear',
      'diameter',
      'numberOfBolts',
      'boltCircle',
      'etFront',
      'etRear',
      'hubHole',
    ];
    // Merge the "Wheel" (diameter) and "Wheel Width" (rimWidthFront /
    // rimWidthRear) filter tabs into one CombinedWheelFilter panel. The
    // width keys are absorbed into the diameter panel only when both the
    // diameter and front-width configs exist in the Console config —
    // otherwise we fall back to rendering them as separate panels.
    const diameterConfig = availableFilters.find(c => c.key === 'diameter');
    const widthFrontConfig = availableFilters.find(c => c.key === 'rimWidthFront');
    const widthRearConfig = availableFilters.find(c => c.key === 'rimWidthRear');
    const combinedWheelConfigs =
      diameterConfig && widthFrontConfig
        ? { diameterConfig, widthFrontConfig, widthRearConfig }
        : null;
    const absorbedIntoWheel = combinedWheelConfigs ? ['rimWidthFront', 'rimWidthRear'] : [];

    const selectConfig = () => {
      let selectedConfig = [];

      availableFilters.forEach(config => {
        if (toggleOwnedKeys.includes(config.key)) return;
        // Width keys live inside CombinedWheelFilter, not as standalone panels.
        if (absorbedIntoWheel.includes(config.key)) return;

        if (value === 1) {
          tireKeys.includes(config.key) ? selectedConfig.push(config) : null;
        } else if (value === 2) {
          rimKeys.includes(config.key) ? selectedConfig.push(config) : null;
        } else {
          selectedConfig.push(config);
        }
      });

      return selectedConfig;
    };

    /* When SearchPage is filtered by a specific seller (authorId query
     * param), pull that seller's data out of the first listing's
     * denormalized `author` relationship and render a compact profile
     * card in the breadcrumb bar's right slot — reads as the seller's
     * "store" header. authorId isn't a declared filter so it's read
     * straight from the URL rather than validQueryParams. */
     const rawParams = parse(location.search);
     const filteredAuthorId = rawParams?.authorId;
     const filteredAuthor =
       filteredAuthorId && listings && listings.length > 0
         ? ensureUser(listings[0].author)
         : null;
     const filteredAuthorProfile = filteredAuthor?.attributes?.profile;
     const sellerCard =
       filteredAuthor && filteredAuthor.id && filteredAuthorProfile?.displayName ? (
         <NamedLink {...profilePageLinkProps(filteredAuthor)} className={css.sellerCard}>
           <AvatarMedium user={filteredAuthor} disableProfileLink />
           <div className={css.sellerMeta}>
             <span className={css.sellerName}>{filteredAuthorProfile.displayName}</span>
             <span className={css.sellerType}>
               {filteredAuthorProfile.publicData?.userType === 'business'
                 ? 'Återförsäljare'
                 : 'Privatperson'}
             </span>
           </div>
           <span className={css.sellerViewProfile}>Visa profil →</span>
         </NamedLink>
       ) : null;

    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
        noindex={noindex}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        {/* Desktop chrome: on a phone the trail + intent header pushed the
          * first listing down a page whose above-fold budget is 300px
          * (P25009-453 AC-2). display:none, so crawlers still get both. */}
        <div className={css.breadcrumbsDesktopOnly}>
        <Breadcrumbs
          trail={[
            { label: 'Hem', name: 'LandingPage' },
            make || intentCrumbs.length > 0
              ? { label: 'Annonser', name: 'SearchPage' }
              : { label: 'Annonser' },
            ...(make
              ? [
                  model
                    ? { label: makeLabel, name: 'SearchPageMake', params: { make } }
                    : { label: makeLabel },
                ]
              : []),
            ...(model ? [{ label: modelLabel }] : []),
            /* Hem · Annonser · Däck · Vinter · 235/55R19 — the filter parts
             * of the intent headline, as plain labels (BR-1). */
            ...intentCrumbs,
          ]}
          rightSlot={sellerCard}
        >
          {/* A dimension page already has its own H1 further down, next to the
            * listing count and intro ("Däck 205/55 R16"). Rendering this one too
            * gave those pages two H1s with the generic, identical-everywhere one
            * first — so on /dack/205-55r16 the first heading a crawler met said
            * "Alla annonser". Leave the specific heading to be the only one. */}
          {dimensionHeading ? null : (
            <div className={css.intentHeader}>
              <div className={css.intentHeaderText}>
                <h1 className={css.pageTitle}>
                  {intentHeadline ||
                    (model || make
                      ? `${makeLabel || ''}${model ? ` ${modelLabel}` : ''}`
                      : 'Alla annonser')}
                </h1>
                {intentSubline ? <p className={css.intentSubline}>{intentSubline}</p> : null}
              </div>
              {/* "Bevaka" is in the results toolbar since P25009-491 (BR-5). */}
            </div>
          )}
        </Breadcrumbs>
        </div>
        <PopularModels make={make} model={model} />
        {/* A remembered car turns the plain search into its car search. */}
        <RememberedCarSearch />
        {/* The car, once, above the filters and the results — full width on a
          * computer, the first card under the header on a phone (P25009-491
          * BR-1). The sidebar's "Din bil" box, the phone sheet's DIN BIL box
          * and the "Dina bilar" chip row it replaces are gone. */}
        <div className={css.carBarContainer}>
          <CarBar activeType={activeAdType} onSelectType={handleSelectAdType} />
        </div>
        <div className={css.layoutWrapperContainer}>
          <aside className={css.layoutWrapperFilterColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {/* "Filter · n aktiva" folds the whole card (P25009-486 BR-2);
                * each group inside folds on its own and shows its choice. */}
              <div className={css.filterHeaderBar}>
                <button
                  type="button"
                  className={css.filterHeaderToggle}
                  aria-expanded={this.state.filterPanelOpen}
                  onClick={() =>
                    this.setState(state => ({ filterPanelOpen: !state.filterPanelOpen }))
                  }
                >
                  <span className={css.filterHeaderTitle}>Filter</span>
                  {activeFilterCount > 0 ? (
                    <span className={css.filterHeaderCount}>{activeFilterCount} aktiva</span>
                  ) : null}
                  <svg
                    className={classNames(css.filterHeaderChevron, {
                      [css.filterHeaderChevronOpen]: this.state.filterPanelOpen,
                    })}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {this.state.filterPanelOpen ? (
                  <button
                    type="button"
                    className={css.filterHeaderReset}
                    onClick={e => this.handleResetAll(e)}
                  >
                    <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                  </button>
                ) : null}
              </div>
              {this.state.filterPanelOpen ? (
                <>
                  {/* Season, tyre type, condition, size, bolt pattern, ET, hub
                    * bore, rim type and price — the same groups in the same
                    * order as the phone's filter sheet (P25009-491 BR-5). The
                    * product is chosen in the car bar, never here. */}
                  <DimensionQuickSearch
                    collapsible
                    hideTypeToggle
                    activeType={activeAdType}
                    onSelectType={handleSelectAdType}
                    hubHoleConfig={hubHoleConfig}
                    conditionConfig={conditionConfig}
                    rimTypeConfig={rimTypeConfig}
                    priceConfig={priceFilterConfig}
                  />
                  {selectConfig().map(config => {
                    return (
                      <FilterComponent
                        key={config.key}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={config}
                        combinedWheelConfigs={
                          config.key === 'diameter' && value !== 1 ? combinedWheelConfigs : null
                        }
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        liveEdit
                        showAsPopup={false}
                        isDesktop
                      />
                    );
                  })}
                </>
              ) : null}
            </div>
          </aside>

          <div className={css.layoutWrapperMain} role="main">
            <div className={css.searchResultContainer}>
              <div className={css.searchWithCardWrapper}>
                <div className={css.searchColumnSpacer} />
                <div className={css.searchColumn}>
                  {/* <TopbarSearchForm
                    className={css.searchLink}
                    desktopInputRoot={css.topbarSearchWithLeftPadding}
                    onSubmit={(values) => this.handleSubmit(values, urlQueryParams)}
                    initialValues={initialSearchFormValues}
                    appConfig={config}
                  />
                  <SearchSection /> */}
                  {/* <WheelplaceSearchCard
                    initialKeywords={validQueryParams?.keywords || ''}
                  /> */}
                </div>
              </div>
              {/* <RecommendedDimensionsCard /> */}
              {/* The phone's car card is the car bar above (P25009-491). */}
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                resultsNoun={productNoun}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                saveSearchComponent={<SaveSearchButton label="Bevaka" withIcon iconOnly />}
                onClearPills={pills.length > 0 ? () => this.removePills(pills) : null}
                viewToggleComponent={
                  <ViewToggle
                    mode={this.state.viewMode}
                    onChange={this.handleViewModeChange}
                  />
                }
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                pills={pills}
                onRemovePill={this.handleRemovePill}
              >
                {/* P25009-432 — every filter lives inside the sheet, as
                  * collapsible groups. The same groups in the same order as
                  * the computer's sidebar, and no DIN BIL box: the car is in
                  * the car bar (P25009-491 BR-5, AC-6). */}
                <DimensionQuickSearch
                  collapsible
                  hideTypeToggle
                  activeType={activeAdType}
                  onSelectType={handleSelectAdType}
                  hubHoleConfig={hubHoleConfig}
                  conditionConfig={conditionConfig}
                  rimTypeConfig={rimTypeConfig}
                  priceConfig={priceFilterConfig}
                />
                {selectConfig().map(config => {
                  /* BR-5 — in the sheet, a group whose filter is set is
                   * always open (its value is visible), and the "place"
                   * group buyers actually use starts open too. */
                  const paramName = config.key === 'price' ? 'price' : `pub_${config.key}`;
                  const hasUrlValue = validQueryParams[paramName] != null;
                  return (
                    <FilterComponent
                      key={config.key}
                      idPrefix="SearchFiltersMobile"
                      config={config}
                      combinedWheelConfigs={
                        config.key === 'diameter' && value !== 1 ? combinedWheelConfigs : null
                      }
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                      initiallyOpen={hasUrlValue || config.key === 'county'}
                    />
                  );
                })}
              </SearchFiltersMobile>
              {/* <div>
                <Tabs value={value} onChange={handleChange} centered>
                  <Tab label="Hjul" />
                  <Tab label="Däck" />
                  <Tab label="Fälg" />
                </Tabs>
                <div role="tabpanel" hidden={value !== 0}>
                  {value === 0 && <ListingsPanel listingType="wheel" />}
                </div>
                <div role="tabpanel" hidden={value !== 1}>
                  {value === 1 && <ListingsPanel listingType="tire" />}
                </div>
                <div role="tabpanel" hidden={value !== 2}>
                  {value === 2 && <ListingsPanel listingType="rim" />}
                </div>
              </div> */}
              <ListingsPanel />
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
};

SearchPageComponent.propTypes = {
  listings: array,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { make, model, tireSize, rimSize } = useParams();


  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      make={make}
      model={model}
      tireSize={tireSize}
      rimSize={rimSize}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    currentPageAdverttsIds,
    connectedUsers,
    getConnectedUsersInProgress,
    userListingCounts,
    fetchUserListingCountsInProgress,
    getSavedQueriesInProgress,
    getSavedQueriesError,
    savedQueries,
    editingQueryId,
    updateSavedQueryInProgress,
    relaxedFilters,
    fitResult,
    searchLoadPending,
    listingNumberMatch,
  } = state.SearchPage;

  const { currentUser } = state.user;
  const licensePlateInfo = state.currentLicensePlate?.currentLicensePlate;
  const licensePlateReg = state.currentLicensePlate?.currentLicensePlateReg || null;
  const car = state.car;
  const listingType = state.listingType;
  let listings = getListingsById(state, currentPageResultIds);
  const adverts = getListingsById(state, currentPageAdverttsIds);

  // listings = listings.filter(listing => {
  //   if (
  //     listing.attributes.publicData.isHide &&
  //     currentUser &&
  //     listing.author.id.uuid === currentUser.id.uuid
  //   ) {
  //     return listing;
  //   }
  //   if (!listing.attributes.publicData.isHide) {
  //     return listing;
  //   }
  // });

  let filtePagination = {
    ...pagination,
    // totalItems: listings ? listings.length : 0,
  };
  return {
    listings,
    relaxedFilters: relaxedFilters || [],
    fitResult: fitResult || null,
    searchLoadPending: !!searchLoadPending,
    listingNumberMatch: listingNumberMatch || null,
    pagination: filtePagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    licensePlateInfo,
    licensePlateReg,
    car,
    listingType,
    currentUser,
    adverts,
    connectedUsers,
    getConnectedUsersInProgress,
    userListingCounts,
    fetchUserListingCountsInProgress,
    getSavedQueriesInProgress,
    getSavedQueriesError,
    savedQueries,
    editingQueryId,
    updateSavedQueryInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  OnUpdateSavedQuery: (params) => dispatch(updateUserSavedQuery(params)),
  onRefreshSearch: (params, search, config) => dispatch(loadData(params, search, config)),
  onClearCurrentLicensePlate: () => {
    dispatch({
      type: 'CLEAR_CURRENT_LICENSE_PLATE',
    });
  },
  onClearCar: () => {
    dispatch({
      type: 'CLEAR_CAR',
    });
  },
  onClearListingType: () => {
    dispatch({
      type: 'CLEAR_LISTINGTYPE',
    });
  },
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
